import { Divider, FormControlLabel, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import ImageUploaderWithPreview from "@entrilia/shared/components/imageUploader/ImageUploaderWithPreview";
import ImagePlaceholderIcon from "@entrilia/shared/icons/ImagePlaceholderIcon";
import type { FundPortalCoreBrandingSettings, RelativeLogoSize } from "../../../../api/types/investorPortalTypes";
import EditColorSection from "./EditColorSection";
import type { PortalStyleSettings } from "./investorPortalStyleState";
import { logoTypeHeights } from "./investorPortalStyleState";
import SectionTitle from "./SectionTitle";

interface Props {
  settings: PortalStyleSettings;
  portalSettings: FundPortalCoreBrandingSettings;
  disabled: boolean;
  isSaving: boolean;
  onChange: (update: Partial<PortalStyleSettings>) => void;
}

const imageTypes = ["image/png", "image/jpeg", "image/svg+xml"];
const faviconImageTypes = ["image/png", "image/x-icon", "image/svg+xml"];

const PortalPagesSettings = ({ settings, portalSettings, disabled, isSaving, onChange }: Props) => (
  <Stack spacing={4}>
    <Stack spacing={2}>
      <SectionTitle text="Logotype on Navigation Bar" description="PNG, SVG, JPG. Recommended size: 280x56 pixels." />
      <ImageUploaderWithPreview
        disabled={disabled || isSaving}
        width="100%"
        height={68}
        svgFallbackSize={{ width: 280, height: 56 }}
        backgroundSize={`auto ${logoTypeHeights[settings.logoTypeSize]}px`}
        allowedImageTypes={imageTypes}
        originalImageSrc={portalSettings.logoTypeUrl}
        imageSrc={settings.logoTypeUrl}
        onImageSelected={(logoTypeFile, logoTypeUrl) => onChange({ logoTypeFile, logoTypeUrl })}
        uploadButtonName="Upload"
        backgroundColor={settings.appBarColor}
        maxFileSizeBytes={500_000}
        buttonsPosition="bottom"
        Placeholder={
          <Stack sx={{ p: 2 }}>
            <Typography variant="h5" color="textDisabled">
              Logotype
            </Typography>
          </Stack>
        }
      />
      <Stack spacing={1}>
        <Typography variant="subtitle2">Logotype size</Typography>
        <RadioGroup
          row
          value={settings.logoTypeSize}
          onChange={(e) => onChange({ logoTypeSize: e.target.value as RelativeLogoSize })}
        >
          <FormControlLabel control={<Radio />} value="Small" label="Small" disabled={disabled} />
          <FormControlLabel control={<Radio />} value="Medium" label="Medium" disabled={disabled} />
          <FormControlLabel control={<Radio />} value="Large" label="Large" disabled={disabled} />
        </RadioGroup>
      </Stack>
    </Stack>

    <Stack spacing={2}>
      <SectionTitle text="Company logomark" description="PNG, SVG, JPG. Recommended size: 56x56 pixels." />
      <ImageUploaderWithPreview
        disabled={disabled || isSaving}
        width={56}
        height={56}
        allowedImageTypes={imageTypes}
        originalImageSrc={portalSettings.logoMarkUrl}
        imageSrc={settings.logoMarkUrl}
        onImageSelected={(logoMarkFile, logoMarkUrl) => onChange({ logoMarkFile, logoMarkUrl })}
        uploadButtonName="Upload"
        maxFileSizeBytes={500_000}
        buttonsPosition="right"
        border="dashed"
        Placeholder={
          <Stack sx={{ p: 2 }}>
            <ImagePlaceholderIcon />
          </Stack>
        }
      />
    </Stack>

    <Stack spacing={2}>
      <SectionTitle text="Favicon" description="PNG, SVG, ICO. Recommended size: 16x16 pixels or 32x32 pixels." />
      <ImageUploaderWithPreview
        disabled={disabled || isSaving}
        width={32}
        height={32}
        allowedImageTypes={faviconImageTypes}
        originalImageSrc={portalSettings.faviconUrl}
        imageSrc={settings.faviconUrl}
        onImageSelected={(faviconFile, faviconUrl) => onChange({ faviconFile, faviconUrl })}
        uploadButtonName="Upload"
        maxFileSizeBytes={500_000}
        border="dashed"
        buttonsPosition="right"
        Placeholder={
          <Stack sx={{ p: 0.5 }}>
            <ImagePlaceholderIcon />
          </Stack>
        }
      />
    </Stack>

    <Divider flexItem />

    <Stack spacing={3}>
      <SectionTitle text="Navigation colors" description="Choose a color scheme for the main horizontal bar." />
      <EditColorSection
        title="Navigation bar color"
        subtitle="Background color of the main navigation bar."
        color={settings.appBarColor}
        onChange={(appBarColor) => onChange({ appBarColor })}
        disabled={disabled}
      />
      <EditColorSection
        title="Navigation text color"
        subtitle="Text color of the main navigation bar."
        color={settings.appBarTextColor}
        onChange={(appBarTextColor) => onChange({ appBarTextColor })}
        disabled={disabled}
      />
      <EditColorSection
        title="Highlights"
        subtitle="Color of buttons and highlight color for the selected navigation item."
        color={settings.mainColor}
        onChange={(mainColor) => onChange({ mainColor })}
        disabled={disabled}
      />
    </Stack>

    <Divider flexItem />

    <Stack spacing={3}>
      <SectionTitle
        text="Banner"
        textAppend="(Investor Portal Only)"
        description="Banner will be displayed on first page on the portal. PNG, SVG or JPG. Recommended size: 2560x120 pixels."
      />
      <ImageUploaderWithPreview
        disabled={disabled || isSaving}
        width="100%"
        height={56}
        svgFallbackSize={{ width: 2560, height: 120 }}
        allowedImageTypes={imageTypes}
        originalImageSrc={portalSettings.bannerUrl}
        imageSrc={settings.bannerUrl}
        onImageSelected={(bannerFile, bannerUrl) => onChange({ bannerFile, bannerUrl })}
        uploadButtonName="Upload"
        backgroundSize="cover"
        maxFileSizeBytes={1_000_000}
        buttonsPosition="bottom"
      />
      <EditColorSection
        title="Text Color"
        subtitle="Text color on the banner."
        color={settings.bannerFontColor}
        onChange={(bannerFontColor) => onChange({ bannerFontColor })}
        disabled={disabled}
      />
    </Stack>
  </Stack>
);

export default PortalPagesSettings;
